import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      return toast.error(error.message);
    }
    if (!data.user?.email_confirmed_at) {
      setLoading(false);
      toast.info("Please verify your email to continue");
      navigate("/verify-email");
      return;
    }
    // Wait for Supabase to persist the session to localStorage. Without this,
    // navigate("/app") can fire before the AuthProvider sees the new session,
    // and RequireAuth bounces the user back to /login. We poll getSession()
    // briefly (max ~2s) until the session is materialized in storage.
    for (let i = 0; i < 20; i++) {
      const { data: s } = await supabase.auth.getSession();
      if (s.session?.access_token) break;
      await new Promise((r) => setTimeout(r, 100));
    }
    setLoading(false);
    navigate("/app");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[35%_65%] bg-[#FAF8F5]">
      <div className="flex flex-col justify-center px-8 md:px-14 py-12">
        <Link to="/" className="mb-10 inline-block">
          <Logo variant="full" size={40} />
        </Link>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1C1917] mb-2">Welcome back</h1>
        <p className="text-[#57534E] mb-8">Sign in to your workspace.</p>

        <form onSubmit={submit} className="space-y-5 max-w-md">
          <div>
            <Label htmlFor="email">Work email</Label>
            <Input id="email" data-testid="login-email" type="email" required autoComplete="email"
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   className="mt-1.5 bg-white border-[#E8E2D9]" placeholder="you@company.com" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" data-testid="link-forgot" className="text-xs text-[#E8712C] font-medium">Forgot?</Link>
            </div>
            <Input id="password" data-testid="login-password" type="password" required autoComplete="current-password"
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   className="mt-1.5 bg-white border-[#E8E2D9]" placeholder="••••••••" />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading}
                  className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F] py-6">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="text-sm text-[#57534E] mt-6">
          New here? <Link to="/signup" data-testid="link-signup" className="text-[#E8712C] font-medium">Create an account</Link>
        </p>
        <p className="text-xs text-[#A8A29E] mt-8 max-w-md leading-relaxed">
          <Link to="/privacy" className="hover:text-[#E8712C]">Privacy</Link> · <Link to="/terms" className="hover:text-[#E8712C]">Terms</Link> · <Link to="/data-deletion" className="hover:text-[#E8712C]">Data Deletion</Link> · <a href="mailto:support@clientdeskva.com" className="hover:text-[#E8712C]">Support</a>
        </p>
      </div>
      <div className="hidden lg:block bg-gradient-to-br from-[#334E68] to-[#3F5E7B]" />
    </div>
  );
}
